import * as React from 'react';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import type {
    CalendarPrintPageProps,
    CalendarPrintPageStyleProps,
    CalendarPrintPageStyles,
} from './CalendarPrintPage.types';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { formatPrintRange } from './formatPrintRange';
import { Icon } from '@fluentui/react/lib/Icon';
import { MiniCalendars } from './MiniCalendars';
import loc from 'owa-localize';
import { calendarPrintPageNotesLabel } from './CalendarPrintPage.base.locstring.json';
import { ControlIcons } from 'owa-control-icons';
import { DateRangeType } from '..';

const getClassNames = classNamesFunction<CalendarPrintPageStyleProps, CalendarPrintPageStyles>();

/** The base non-styled version of CalendarPrintPage. */
export function CalendarPrintPageBase(props: CalendarPrintPageProps) {
    // Styles
    const {
        styles,
        className,
        padding = '30px',
        contentCanGrow,
        preview,
        miniMonth,
        notes,
    } = props;
    const classNames = getClassNames(styles, {
        className: className,
        padding: padding!,
        contentCanGrow: contentCanGrow!,
        preview: preview!,
    });

    // Properties
    const { title, calendarView, children, calendarColorScheme } = props;

    // Computed Properties
    const dateText = formatPrintRange(calendarView);

    // Icon color style
    const iconColorStyle =
        calendarColorScheme.length == 1 ? { color: calendarColorScheme[0].calPrimary } : {};

    const content = (
        <div className={classNames.pageBody}>
            <div className={classNames.inputBlocker} />
            {children}
        </div>
    );

    const headerWithMiniMonthStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'row',
    };

    return (
        <div className={classNames.root}>
            <FocusZone className={classNames.pageHeader} disabled={true}>
                <div className={classNames.inputBlocker} />
                <div style={miniMonth ? headerWithMiniMonthStyle : null}>
                    <div className={classNames.calendarHeader}>
                        <div className={classNames.previewHeaders}>{dateText}</div>
                        <div className={classNames.previewHeaders}>
                            <Icon
                                className={classNames.icon}
                                iconName={ControlIcons.Calendar}
                                style={iconColorStyle}
                            />
                            {createTitle(title, calendarColorScheme)}
                        </div>
                    </div>
                    {miniMonth && <MiniCalendars {...calendarView} />}
                </div>
            </FocusZone>
            {notes && calendarView.view === DateRangeType.Day ? (
                <div className={classNames.eventsContainer}>
                    {content}
                    <div className={classNames.notesSection}>
                        {loc(calendarPrintPageNotesLabel)}
                    </div>
                </div>
            ) : (
                content
            )}
        </div>
    );
}

function createTitle(title: string[], calendarColorScheme: ColorScheme[]): JSX.Element[] {
    let styledTitle = [];
    let index = 0;

    if (title) {
        title.forEach(calTitle => {
            let result = index == title.length - 1 ? calTitle : calTitle + ', ';
            styledTitle.push(
                <div key={index} style={{ color: calendarColorScheme[index].calPrimary }}>
                    {result}
                </div>
            );
            index++;
        });
    }
    return styledTitle;
}
